import React, { useMemo } from 'react';

import { Block } from '@bcp-channels-sharedcomponents-reactjs/core';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import {
	Image,
	DividerLine,
	TextBlock,
	Typography,
	useTheme,
	Link,
} from '@bcp-nextgen-dx-component-factory/design-system';
import { NavRightO } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { Card } from '@components';
import { rem } from '@helpers';
import { appThemeBcp } from '@themes';

interface AccountRowProps {
	account: {
		name: string;
		number: string;
	};
	accountAvailableBalance: {
		amount: number;
		currency: string;
	};
	accountAccountableBalance: {
		amount: number;
		currency: string;
	};
	changeAccountHandler?: () => void;
}

interface AccountRowResources {
	accountBalance: string;
	accountUsedBalance: string;
	changeAccountHandler: string;
}

export const AccountRow: React.FC<AccountRowProps> = ({
	changeAccountHandler,
	account,
	accountAvailableBalance,
	accountAccountableBalance,
}) => {
	const { spacing } = useTheme();
	const t = useTranslation();

	const resources = useMemo((): AccountRowResources => {
		return {
			accountBalance: t('component.card.accountRow.accountBalance'),
			accountUsedBalance: t('component.card.accountRow.accountUsedBalance'),
			changeAccountHandler: t('component.card.accountRow.button.changeAccount'),
		};
	}, [t]);

	return (
		<Card.Base
			style={{ transition: 'all 400ms', width: '100%' }}
			innerPadding="1.5rem 1rem 1.5rem 1.5rem"
			colorfulBorder={{ visible: true }}
		>
			{/* Account Information */}
			<Block style={{ width: '50%', gap: rem(16) }} align={{ vertical: 'center' }}>
				<Image
					source={appThemeBcp.images.bcpRound.src}
					svgProps={{ width: appThemeBcp.images.bcpRound.width, height: appThemeBcp.images.bcpRound.height }}
				/>
				<Block stack style={{ gap: rem(4) }}>
					<Typography appearance="body-100-03" numberOfLines={1}>
						{account.name}
					</Typography>
					<Typography appearance="body-100-02">{account.number}</Typography>
				</Block>
			</Block>

			{/* Balance */}
			<Block
				align={{ horizontal: 'end' }}
				style={{
					width: '50%',
					textAlign: 'end',
					gap: rem(32),
				}}
			>
				<Block stack style={{ gap: rem(8) }}>
					<Typography appearance="microcopy-75-07" colorToken="secondary-light" numberOfLines={1}>
						{resources.accountBalance}
					</Typography>
					<TextBlock.Currency
						floatValue={accountAvailableBalance.amount}
						size="xSmall"
						currency={accountAvailableBalance.currency}
						signed={accountAvailableBalance.amount < 0 ? true : false}
					/>
				</Block>

				<Block stack style={{ gap: rem(8) }}>
					<Typography appearance="microcopy-75-07" colorToken="secondary-light" numberOfLines={1}>
						{resources.accountUsedBalance}
					</Typography>
					<TextBlock.Currency
						floatValue={accountAccountableBalance.amount}
						size="xSmall"
						currency={accountAccountableBalance.currency}
						signed={accountAccountableBalance.amount < 0 ? true : false}
					/>
				</Block>
			</Block>

			{changeAccountHandler && (
				<Block
					style={{
						alignItems: 'center',
					}}
				>
					<DividerLine margin={`0 ${spacing.s8}`} direction="vertical" />
					<Link
						appearance="primary"
						middleContent={resources.changeAccountHandler}
						numberOfLines={1}
						size="medium"
						textCase="default"
						variant="light"
						onPress={changeAccountHandler}
						endContent={NavRightO}
					/>
				</Block>
			)}
		</Card.Base>
	);
};
